import { Checkbox } from 'antd';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { Button, Input, MaskedInput } from '@/components/Shared/ui';
import { PHONE_NUMBER_REGEX } from '@/constants';
import AlertCircleIcon from '@/media/icons/alert-circle.svg?react';

import type { ClientFormValues, TAgreementName } from '../../types/client-form';

interface IProps {
  classes: {
    actions: string;
    form: string;
  };
  methods: UseFormReturn<ClientFormValues, unknown, ClientFormValues>;
  clientFormLoading: boolean;
  clientError: string;
  handleClientFormSubmit: (values: ClientFormValues) => Promise<void>;
  mainAgreements?: {
    policyLink: string;
    agreementLink: string;
  };
  additionalAgreements?: {
    name: TAgreementName;
    agreementLink: string;
    i18nKey: string;
  }[];
}

const ClientForm: React.FC<IProps> = ({
  classes,
  methods,
  clientFormLoading,
  clientError,
  handleClientFormSubmit,
  mainAgreements,
  additionalAgreements
}) => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    handleSubmit
  } = methods;

  return (
    <form
      className={classes.form}
      onSubmit={handleSubmit(handleClientFormSubmit)}
    >
      <Controller
        control={control}
        name="iin"
        rules={{
          required: t('common.form.validation.required-2'),
          minLength: {
            value: 12,
            message: t('common.form.validation.iin-error')
          }
        }}
        render={({ field }) => (
          <Input
            {...field}
            type="tel"
            inputMode="numeric"
            label={t('common.form.labels.iin')}
            value={field.value ?? ''}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              if (value.length > 12) return;
              field.onChange(value);
            }}
            error={errors.iin?.message}
            isRequired
            className="ym-disable-keys"
          />
        )}
      />
      <Controller
        control={control}
        name="phone"
        rules={{
          required: t('common.form.validation.required-2'),
          validate: (value) => {
            if (!value.match(PHONE_NUMBER_REGEX))
              return t('common.form.validation.phone-error');
          }
        }}
        render={({ field }) => (
          <MaskedInput
            {...field}
            type="tel"
            label={t('common.form.labels.phone-number')}
            value={field.value ?? ''}
            error={errors.phone?.message}
            mask="+7 (___) ___-__-__"
            isRequired
            autoComplete="off"
            className="ym-disable-keys"
          />
        )}
      />

      {clientError && (
        <p className="client-otp-modal__error" role="alert">
          <AlertCircleIcon />
          {clientError || t('common.error.base-2')}
        </p>
      )}

      {mainAgreements && (
        <Controller
          control={control}
          name="policyAgreement"
          rules={{
            required: t('otp-validation.policy-agreement-required')
          }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <Checkbox
                {...field}
                checked={field.value}
                onChange={(e) => {
                  field.onChange(e.target.checked);
                }}
              >
                <Trans
                  i18nKey={'otp-validation.policy-agreement'}
                  components={{
                    1: (
                      <a
                        className="client-otp-modal__link"
                        href={mainAgreements.policyLink}
                        download
                        target="_blank"
                      />
                    ),
                    2: (
                      <a
                        className="client-otp-modal__link"
                        href={mainAgreements.agreementLink}
                        download
                        target="_blank"
                      />
                    )
                  }}
                />
              </Checkbox>
              {error && (
                <p className="client-otp-modal__field-error">{error.message}</p>
              )}
            </div>
          )}
        />
      )}

      {additionalAgreements &&
        additionalAgreements.map((agreement) => (
          <Controller
            key={agreement.name}
            control={control}
            name={agreement.name}
            rules={{
              required: t('otp-validation.policy-agreement-required')
            }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Checkbox
                  {...field}
                  checked={field.value}
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                  }}
                >
                  {
                    <Trans
                      i18nKey={agreement.i18nKey}
                      components={{
                        1: (
                          <a
                            className="client-otp-modal__link"
                            href={agreement.agreementLink}
                            download
                            target="_blank"
                          />
                        )
                      }}
                    />
                  }
                </Checkbox>
                {error && (
                  <p className="client-otp-modal__field-error">
                    {error.message}
                  </p>
                )}
              </div>
            )}
          />
        ))}

      <div className={classes.actions}>
        <Button
          disabled={Object.values(errors).length !== 0}
          variant="filled-primary"
          isLoading={clientFormLoading}
        >
          {t('otp-validation.continue')}
        </Button>
      </div>
    </form>
  );
};

export default ClientForm;
